import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../shared/api/axiosInstance';
import type { InboxResponse, UnreadCountResponse, InboundEmail, ListInboxParams, MyAddressResponse, MyAddressesResponse } from '../types';

export const INBOX_QUERY_KEY = 'inbox';
const UNREAD_COUNT_KEY = 'inbox-unread-count';
const MY_ADDRESS_KEY = 'my-email-address';
const MY_ADDRESSES_KEY = 'my-email-addresses';

export function useInbox(params: ListInboxParams = {}) {
  return useQuery<InboxResponse>({
    queryKey: [INBOX_QUERY_KEY, params],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/v1/emails/inbox', { params });
      return response.data;
    },
  });
}

export function useUnreadCount(options: { enabled?: boolean } = {}) {
  return useQuery<UnreadCountResponse>({
    queryKey: [UNREAD_COUNT_KEY],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/v1/emails/inbox/unread-count');
      return response.data;
    },
    refetchInterval: 60000, // Refresh every minute
    enabled: options.enabled ?? true,
  });
}

export function useEmailDetail(emailId: string | null) {
  return useQuery<InboundEmail>({
    queryKey: [INBOX_QUERY_KEY, 'detail', emailId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/api/v1/emails/inbox/${emailId}`);
      return response.data.data;
    },
    enabled: !!emailId,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId: string) => {
      await axiosInstance.post(`/api/v1/emails/inbox/${emailId}/mark-read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] });
    },
  });
}

export function useMarkUnread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId: string) => {
      await axiosInstance.post(`/api/v1/emails/inbox/${emailId}/mark-unread`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] });
    },
  });
}

export function useDeleteEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailId: string) => {
      await axiosInstance.delete(`/api/v1/emails/inbox/${emailId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] });
    },
  });
}

export function useMyEmailAddress() {
  return useQuery<MyAddressResponse>({
    queryKey: [MY_ADDRESS_KEY],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/v1/emails/my-address');
      return response.data;
    },
  });
}

export function useMyEmailAddresses() {
  return useQuery<MyAddressesResponse>({
    queryKey: [MY_ADDRESSES_KEY],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/v1/emails/my-addresses');
      return response.data;
    },
  });
}

export function useCreateMyEmailAddresses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.post('/api/v1/emails/my-addresses');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MY_ADDRESS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_ADDRESSES_KEY] });
    },
  });
}

interface RefreshResponse {
  new: number;
  skipped: number;
  errors: number;
}

export function useRefreshInbox() {
  const queryClient = useQueryClient();

  return useMutation<RefreshResponse>({
    mutationFn: async () => {
      const response = await axiosInstance.post('/api/v1/emails/inbox/refresh');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] });
    },
  });
}

/**
 * Background "pull new mail" used to auto-sync the user's inbox when it is opened.
 *
 * Deliberately NOT a `useMutation`: a mutation fired from a mount `useEffect` gets
 * its observer detached from the in-flight mutation under React 18 StrictMode
 * (mount → cleanup → mount), so `isPending` sticks `true` forever — which left the
 * inbox refresh button spinning permanently in dev. A plain async call plus manual
 * invalidation has no observer to get stuck. The user-facing refresh button keeps
 * using `useRefreshInbox` (a click never tears its observer down mid-flight).
 *
 * Returns a stable fire-and-forget function; pull failures are non-fatal (the inbox
 * still shows already-cached mail).
 */
export function useAutoPullInbox() {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    try {
      await axiosInstance.post('/api/v1/emails/inbox/refresh');
      queryClient.invalidateQueries({ queryKey: [INBOX_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] });
    } catch {
      // Background pull is best-effort; surface nothing on failure.
    }
  }, [queryClient]);
}

// Agent inbox hooks
export const AGENT_INBOX_QUERY_KEY = 'agent-inbox';

export function useAgentInbox(agentId: string, params: ListInboxParams = {}) {
  return useQuery<InboxResponse>({
    queryKey: [AGENT_INBOX_QUERY_KEY, agentId, params],
    queryFn: async () => {
      const response = await axiosInstance.get(`/api/v1/emails/agent/${agentId}/inbox`, { params });
      return response.data;
    },
    enabled: !!agentId,
  });
}

export function useAgentEmailDetail(agentId: string, emailId: string | null) {
  return useQuery<InboundEmail>({
    queryKey: [AGENT_INBOX_QUERY_KEY, agentId, 'detail', emailId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/api/v1/emails/agent/${agentId}/inbox/${emailId}`);
      return response.data.data;
    },
    enabled: !!agentId && !!emailId,
  });
}

export function useAgentMarkRead(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (emailId: string) => {
      await axiosInstance.post(`/api/v1/emails/agent/${agentId}/inbox/${emailId}/mark-read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_INBOX_QUERY_KEY, agentId] });
    },
  });
}

export function useAgentMarkUnread(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (emailId: string) => {
      await axiosInstance.post(`/api/v1/emails/agent/${agentId}/inbox/${emailId}/mark-unread`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_INBOX_QUERY_KEY, agentId] });
    },
  });
}

export function useAgentDeleteEmail(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (emailId: string) => {
      await axiosInstance.delete(`/api/v1/emails/agent/${agentId}/inbox/${emailId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AGENT_INBOX_QUERY_KEY, agentId] });
    },
  });
}
